import { Injectable } from '@angular/core';
import {
  API_SERVICES_CONFIG,
  ApiResponseWithQueryContext,
  DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  HttpGETCustomOptions,
  PaginationService,
  PortalHttpClient,
  RiskInsightsConfig,
} from '@grid-ui/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RiskInsightDetail, RiskInsightsCollection, RiskInsightsQueryParams } from '../../../shared-models';
import { ApiRiskInsightDetail, ApiRiskInsightsCollection } from '../models';

/**
 * Abstract Service for accessing the Risk Insights API
 */
@Injectable()
export class RiskInsightsService {
  private readonly riskInsightConfig: RiskInsightsConfig = API_SERVICES_CONFIG.nessie.insights;
  constructor(
    private readonly http: PortalHttpClient,
    private readonly paginationService: PaginationService,
  ) {}

  /**
   * Get recent visited risk insight.
   */
  public getInsightRecentVisited(): Observable<RiskInsightsCollection> {
    return this.http.get<RiskInsightsCollection>(this.riskInsightConfig.recent._configuration, { forceAPICall: true });
  }

  /**
   * Get the specified risk insight.
   *
   * @param id Id of risk insight to retrieve.
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getInsightDetail(id: number, options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS): Observable<RiskInsightDetail> {
    // TODO: Caching will be implemented using ETag, once P2-132 is addressed
    return this.http.get<ApiRiskInsightDetail>(this.riskInsightConfig.detail._configuration, {
      ...options,
      pathParams: { id },
    });
  }

  /**
   * Get a list of all risk insights which a user is entitled to and meet the specified criteria.
   *
   * @param queryParams An optional object with query parameters. Omitting the query parameters object or passing in an empty
   * object {} will return all insights within the user entitlement
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getInsights(
    queryParams: RiskInsightsQueryParams = {},
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<RiskInsightsCollection> {
    return this.http
      .getPaginated<ApiRiskInsightsCollection>(this.riskInsightConfig._configuration, {
        ...options,
        queryParams,
      })
      .pipe(
        map((apiResponseContext) => {
          const riskInsightsCollection: RiskInsightsCollection = {
            total: apiResponseContext.response.total,
            results: apiResponseContext.response.results,
            paginationContext: this.paginationService.getNewPaginationContext(
              apiResponseContext.response.links,
              apiResponseContext.queryParams,
            ),
          };
          return riskInsightsCollection;
        }),
      );
  }

  /**
   * Get a list of all risk insights which a user is entitled to and meet the specified criteria.
   *
   * @param queryParams An optional object with query parameters. Omitting the query parameters object or passing in an empty
   * object {} will return all insights within the user entitlement
   * @param options An optional argument with custom options for the underlying Http GET request
   */
  public getInsightsSearch(
    queryParams: RiskInsightsQueryParams = {},
    options: HttpGETCustomOptions = DEFAULT_HTTP_GET_CUSTOM_OPTIONS,
  ): Observable<RiskInsightsCollection> {
    // TODO: Caching will be implemented using ETag, once P2-132 is addressed
    return this.http
      .getPaginated<ApiRiskInsightsCollection>(this.riskInsightConfig.search._configuration, {
        ...options,
        queryParams,
      })
      .pipe(
        map((apiResponseContext: ApiResponseWithQueryContext<ApiRiskInsightsCollection>) => ({
          total: apiResponseContext.response.total,
          results: apiResponseContext.response.results,
          paginationContext: this.paginationService.getNewPaginationContext(
            apiResponseContext.response.links,
            apiResponseContext.queryParams,
          ),
        })),
      );
  }
}
